import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function Matomo() {
  const location = useLocation();
  const scrollDepthTracked = useRef({});
  const timeOnPageStart = useRef(null);
  const scriptLoaded = useRef(false);
  
  // Configuration Matomo depuis les variables d'environnement
  const MATOMO_URL = process.env.REACT_APP_MATOMO_URL;
  const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID;
  const MATOMO_DISABLED = process.env.REACT_APP_MATOMO_DISABLED === 'true';
  const MATOMO_REQUIRE_CONSENT = process.env.REACT_APP_MATOMO_REQUIRE_CONSENT === 'true';
  const MATOMO_DISABLE_COOKIES = process.env.REACT_APP_MATOMO_DISABLE_COOKIES === 'true';
  const IS_DEV = process.env.NODE_ENV === 'development';

  useEffect(() => {
    // Ne charger Matomo que si configuré et non désactivé
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) {
      if (IS_DEV && !MATOMO_DISABLED) {
        console.log('Matomo non configuré - Variables d\'environnement manquantes');
      }
      return;
    }

    // Éviter le chargement multiple
    if (scriptLoaded.current) {
      return;
    }

    // Initialiser Matomo
    window._paq = window._paq || [];
    const _paq = window._paq;

    // Configurer Matomo
    _paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
    _paq.push(['setSiteId', MATOMO_SITE_ID]);
    _paq.push(['enableLinkTracking']);
    _paq.push(['setDoNotTrack', true]); // Respecter Do Not Track
    _paq.push(['enableHeartBeatTimer', 15]); // Track temps passé toutes les 15s

    // Options RGPD
    if (MATOMO_REQUIRE_CONSENT) {
      _paq.push(['requireConsent']);
    }
    if (MATOMO_DISABLE_COOKIES) {
      _paq.push(['disableCookies']);
    }

    // Charger le script Matomo
    const script = document.createElement('script');
    script.id = 'matomo-script';
    script.async = true;
    script.src = `${MATOMO_URL}/matomo.js`;
    script.onload = () => {
      scriptLoaded.current = true;
      if (IS_DEV) {
        console.log('Matomo chargé avec succès');
      }
    };
    script.onerror = () => {
      if (IS_DEV) {
        console.error('Erreur de chargement du script Matomo');
      }
    };
    document.head.appendChild(script);

    return () => {
      // Nettoyer le script lors du démontage
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      scriptLoaded.current = false;
    };
  }, [MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED, MATOMO_REQUIRE_CONSENT, MATOMO_DISABLE_COOKIES, IS_DEV]);

  // Skip analytics on 404 pages (Bing best practice: no service calls on error pages)
  const isNotFoundPage = () => Boolean(document.querySelector('main.not-found'));

  // Tracker les changements de page
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) return;
    if (isNotFoundPage()) return;

    const _paq = window._paq || [];
    const fullUrl = `${window.location.origin}${location.pathname}${location.search}`;
    
    _paq.push(['setCustomUrl', fullUrl]);
    _paq.push(['setDocumentTitle', document.title]);
    _paq.push(['trackPageView']);

    // Reset scroll depth tracking pour nouvelle page
    scrollDepthTracked.current = {};
    timeOnPageStart.current = Date.now();

    // Cleanup sur changement de page
    return () => {
      if (timeOnPageStart.current) {
        const timeSpent = Math.round((Date.now() - timeOnPageStart.current) / 1000);
        _paq.push(['trackEvent', 'Engagement', 'TimeOnPage', document.title, timeSpent]);
      }
    };
  }, [location, MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED]);

  // Tracking scroll depth
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) return;
    if (isNotFoundPage()) return;

    const handleScroll = () => {
      const scrollPercentage = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      const _paq = window._paq || [];
      const depths = [25, 50, 75, 100];

      depths.forEach(depth => {
        if (scrollPercentage >= depth && !scrollDepthTracked.current[depth]) {
          scrollDepthTracked.current[depth] = true;
          _paq.push(['trackEvent', 'Scroll Depth', `${depth}%`, document.title]);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED]);

  // Tracking des liens externes
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) return;

    const handleClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (href && (href.startsWith('http') && !href.includes(window.location.hostname))) {
        const _paq = window._paq || [];
        _paq.push(['trackEvent', 'External Links', 'Click', href]);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED]);

  // Tracking des erreurs JavaScript
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) return;

    const handleError = (event) => {
      const _paq = window._paq || [];
      _paq.push([
        'trackEvent',
        'JavaScript Error',
        event.message || 'Unknown Error',
        `${event.filename}:${event.lineno}:${event.colno}`
      ]);
    };

    const handleUnhandledRejection = (event) => {
      const _paq = window._paq || [];
      _paq.push([
        'trackEvent',
        'Unhandled Promise Rejection',
        event.reason?.message || 'Unknown Rejection',
        event.reason?.stack || 'No stack trace'
      ]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED]);

  // Tracking Core Web Vitals
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID || MATOMO_DISABLED) return;

    // Observer pour Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          const lcp = lastEntry.renderTime || lastEntry.loadTime;
          
          const _paq = window._paq || [];
          _paq.push(['trackEvent', 'Web Vitals', 'LCP', document.title, Math.round(lcp)]);
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fid = entry.processingStart - entry.startTime;
            const _paq = window._paq || [];
            _paq.push(['trackEvent', 'Web Vitals', 'FID', document.title, Math.round(fid)]);
          });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });

        // Cumulative Layout Shift (CLS)
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });

        // Track CLS au déchargement de la page
        const trackCLS = () => {
          const _paq = window._paq || [];
          _paq.push(['trackEvent', 'Web Vitals', 'CLS', document.title, Math.round(clsValue * 1000)]);
        };

        window.addEventListener('beforeunload', trackCLS);

        return () => {
          lcpObserver.disconnect();
          fidObserver.disconnect();
          clsObserver.disconnect();
          window.removeEventListener('beforeunload', trackCLS);
        };
      } catch (e) {
        if (IS_DEV) {
          console.log('Performance Observer not fully supported');
        }
      }
    }
  }, [MATOMO_URL, MATOMO_SITE_ID, MATOMO_DISABLED, IS_DEV]);

  return null; // Ce composant ne rend rien
}

// Fonction helper pour tracker des événements personnalisés
export const trackEvent = (category, action, name = null, value = null) => {
  if (window._paq) {
    window._paq.push(['trackEvent', category, action, name, value]);
  }
};

// Fonction helper pour tracker des objectifs
export const trackGoal = (goalId) => {
  if (window._paq) {
    window._paq.push(['trackGoal', goalId]);
  }
};

// Fonction helper pour tracker les performances personnalisées
export const trackPerformance = (metricName, value) => {
  if (window._paq) {
    window._paq.push(['trackEvent', 'Performance', metricName, document.title, Math.round(value)]);
  }
};

// Fonction helper pour tracker les interactions utilisateur
export const trackInteraction = (element, action) => {
  if (window._paq) {
    window._paq.push(['trackEvent', 'User Interaction', action, element]);
  }
};

export default Matomo;